import PropTypes from 'prop-types';

import { proseMirrorDocShape, SCOPES } from '../../_shared';

import { FIELD_TYPES, FORM_TYPES } from '../constants';

/**
 * A GalleryP form value object.
 */
export const formValueShape = PropTypes.shape({

	/**
	 * The form value ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The form value data.
	 */
	data: PropTypes.oneOfType([
		PropTypes.bool, // checkbox input
		PropTypes.number, // likert input
		PropTypes.arrayOf(PropTypes.string), // checkboxes input
		PropTypes.string, // all other inputs
	]),

	/**
	 * The user ID.
	 */
	user_id: PropTypes.string.isRequired,

	/**
	 * True, if the form value is the overall correct answer for the form field.
	 */
	is_answer_correct: PropTypes.bool.isRequired,

	/**
	 * The selected form field options for the form value.
	 */
	selected_form_field_options: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The option ID.
		 */
		id: PropTypes.string.isRequired,

	})).isRequired,

});

/**
 * A GalleryP form field option object.
 */
export const formFieldOptionShape = PropTypes.shape({

	/**
	 * The form field option ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The value.
	 */
	value: PropTypes.string.isRequired,

	/**
	 * True, if this option is a correct answer choice.
	 */
	is_correct_answer: PropTypes.bool,

	/**
	 * The multi-language content for the form field option.
	 */
	form_field_option_contents: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The label.
		 */
		label: PropTypes.string,

	})),

});

/**
 * A GalleryP form field object.
 */
export const formFieldShape = PropTypes.shape({

	/**
	 * The form field ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The field type.
	 */
	type: PropTypes.oneOf(Object.values(FIELD_TYPES)).isRequired,

	/**
	 * The multi-language content for the form field.
	 */
	form_field_contents: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The title text.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The placeholder text.
		 */
		placeholder: PropTypes.string,

		/**
		 * The ProseMirror helper text.
		 */
		help: proseMirrorDocShape,

		/**
		 * The ProseMirror correct feedback text on submission.
		 */
		submission_feedback_correct: proseMirrorDocShape,

		/**
		 * The ProseMirror incorrect feedback text on submission.
		 */
		submission_feedback_incorrect: proseMirrorDocShape,

	})),

	/**
	 * The list of options for the form field, if any.
	 */
	form_field_options: PropTypes.arrayOf(formFieldOptionShape),

});

/**
 * A GalleryP form section object.
 */
export const formSectionShape = PropTypes.shape({

	/**
	 * The form section ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The multi-language content for the form section.
	 */
	form_section_contents: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The title.
		 */
		title: PropTypes.string,

		/**
		 * The description.
		 */
		description: PropTypes.string,

	})),

});

/**
 * A GalleryP form object.
 */
export const formShape = PropTypes.shape({

	/**
	 * The form ID.
	 */
	id: PropTypes.string,

	/**
	 * The form type.
	 */
	type: PropTypes.oneOf([
		FORM_TYPES.APPLICATION,
		FORM_TYPES.FORM,
		FORM_TYPES.POLL,
		FORM_TYPES.POST_REGISTRATION,
		FORM_TYPES.PROFILE,
		FORM_TYPES.QUIZ,
	]).isRequired,

	/*
	 * The form name.
	 */
	name: PropTypes.string,

	/**
	 * The form description.
	 */
	description: PropTypes.string,

	/**
	 * True, if learners are allowed to edit form entry submissions.
	 */
	allow_editing_submissions: PropTypes.bool,

	/**
	 * True, if learners are allowed to submit multiple form entries.
	 */
	allow_multiple_submissions: PropTypes.bool,

	/**
	 * The percentage grade needed to pass the quiz (if any).
	 */
	quiz_result_pass_percentage: PropTypes.number,

	/**
	 * True, if learners are allowed to see the correct answers after their submission.
	 */
	quiz_result_show_correct_answers: PropTypes.bool,

	/**
	 * The form width (if any).
	 *
	 * NOTE: Only used by form|poll|quiz resources.
	 */
	width: PropTypes.number,

	/**
	 * The scope.
	 */
	scope: PropTypes.oneOf([
		SCOPES.SESSION,
		SCOPES.COURSE,
		SCOPES.TRACK,
		SCOPES.ORGANIZATION,
	]).isRequired,

	/**
	 * The scope ID.
	 */
	scope_id: PropTypes.string.isRequired,

	/**
	 * The organization ID.
	 */
	organization_id: PropTypes.string,

	/**
	 * The multi-language content for the form.
	 */
	form_contents: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The language.
		 */
		language: PropTypes.string.isRequired,

		/**
		 * The title.
		 */
		name: PropTypes.string,

		/**
		 * The description.
		 */
		description: PropTypes.string,

	})),

	/**
	 * The list of form sections for the form.
	 */
	form_sections: PropTypes.arrayOf(formSectionShape),

	/**
	 * The form fields.
	 */
	form_fields: PropTypes.arrayOf(formFieldShape),

	/**
	 * The form section advanced settings, if any.
	 */
	form_section_advanced_settings: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The form section advanced setting ID.
		 */
		id: PropTypes.string.isRequired,

	})),

});
